export const cleanHtmlForDownload = (html: string, preserveLetterheadFormat: boolean = false): string => {
  // Create a temporary div to parse the HTML
  const tempDiv = window.document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove document title/name at the top if it exists
  const firstElement = tempDiv.querySelector('p:first-child');
  if (firstElement && firstElement.textContent && firstElement.textContent.trim().length < 50) {
    // Check if this might be a title/name (short text, less than 50 chars)
    firstElement.remove();
  }

  // Remove placeholder styling and convert to regular text
  // Add proper spacing around placeholders and ensure they're not bold
  const placeholders = tempDiv.querySelectorAll('.placeholder');
  placeholders.forEach(placeholder => {
    const value = placeholder.textContent || '';
    const span = window.document.createElement('span');
    span.textContent = value;
    span.style.backgroundColor = 'transparent';
    span.style.color = 'black';
    span.style.fontStyle = 'normal';
    span.style.fontWeight = 'normal'; // Ensure text is not bold
    span.style.padding = '0'; // Remove any padding
    placeholder.replaceWith(span);
    
    // Add proper spacing if needed
    // Check if there's no space before the placeholder
    const previousNode = span.previousSibling;
    if (previousNode && previousNode.nodeType === Node.TEXT_NODE && 
        previousNode.textContent && !previousNode.textContent.endsWith(' ')) {
      const textBefore = previousNode.textContent;
      previousNode.textContent = textBefore + ' ';
    }
    
    // Check if there's no space after the placeholder
    const nextNode = span.nextSibling;
    if (nextNode && nextNode.nodeType === Node.TEXT_NODE && 
        nextNode.textContent && !nextNode.textContent.startsWith(' ')) {
      const textAfter = nextNode.textContent;
      nextNode.textContent = ' ' + textAfter;
    }
  });

  // Only add document styling if not preserving letterhead format
  if (!preserveLetterheadFormat) {
    // Add professional document styling with Arial 11pt font
    const style = window.document.createElement('style');
    style.textContent = `
      body {
        font-family: Arial, sans-serif;
        font-size: 11pt;
        line-height: 1.5;
        color: black;
        margin: 0;
        padding: 0;
      }
      
      h1 { font-size: 14pt; font-weight: bold; margin-top: 24pt; margin-bottom: 12pt; }
      h2 { font-size: 12pt; font-weight: bold; margin-top: 20pt; margin-bottom: 10pt; }
      h3 { font-size: 11pt; font-weight: bold; margin-top: 16pt; margin-bottom: 8pt; }
      
      p { 
        margin-bottom: 12pt;
        text-align: justify;
      }
      
      ul, ol { 
        margin-left: 24pt;
        margin-bottom: 12pt;
      }
      
      li { 
        margin-bottom: 6pt;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 12pt;
      }
      
      td, th {
        border: 1px solid #000;
        padding: 6pt;
        text-align: left;
      }
      
      img {
        max-width: 100%;
        height: auto;
      }
      
      .avoid-break {
        page-break-inside: avoid;
      }
      
      .page-break-before {
        page-break-before: always;
      }
      
      .page-break-after {
        page-break-after: always;
      }
      
      .signature-block {
        margin-top: 36pt;
      }
    `;
    tempDiv.prepend(style);
  }

  return tempDiv.innerHTML;
};

/**
 * Converts template HTML with placeholder inputs to a filled document
 * by replacing input elements with their actual values
 */
export function convertPlaceholdersToText(
  htmlContent: string, 
  placeholderValues: Record<string, string> = {}
): string {
  // Parse the HTML content
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Find all placeholder input elements
  const placeholderInputs = doc.querySelectorAll('input.placeholder-input');
  
  placeholderInputs.forEach(input => {
    const inputElement = input as HTMLInputElement;
    const placeholderId = inputElement.getAttribute('data-placeholder');
    const placeholderText = inputElement.getAttribute('placeholder') || '';
    
    if (placeholderId) {
      // Get the actual value from placeholderValues or use placeholder text as fallback
      const actualValue = placeholderValues[placeholderId] || placeholderText;
      
      // Create a text node with the actual value
      const textNode = doc.createTextNode(actualValue);
      
      // Replace the input with the text, preserving any parent formatting
      inputElement.parentNode?.replaceChild(textNode, inputElement);
    }
  });
  
  // Return the processed HTML
  return doc.body ? doc.body.innerHTML : doc.documentElement.innerHTML;
}

/**
 * Converts template HTML to a document preview format
 * - Removes all placeholder inputs and replaces with filled text
 */
export function prepareDocumentForPreview(
  htmlContent: string, 
  placeholderValues: Record<string, string> = {}
): string {
  // Just convert placeholders to text - that's all we need for preview
  return convertPlaceholdersToText(htmlContent, placeholderValues);
}
